import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { map, range, get } from 'lodash-es';
import * as React from 'react';
import ReactSelect, { components, DropdownIndicatorProps } from 'react-select';
import { autoCompletes, ids, placeholders } from './constants';

export const DropdownIndicator = (props: DropdownIndicatorProps<object>) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <span className="react-select-arrow-zone">
          <span
            className={`react-select-arrow-${props.selectProps.menuIsOpen ? 'up' : 'down'}`}
          />
        </span>
      </components.DropdownIndicator>
    )
  );
};

const customStyles = {
  control: (provided: any, state: any) => {
    const hasValueColor = state.hasValue ? '#fff' : '#fffde8';
    return { ...provided, backgroundColor: hasValueColor };
  },
  singleValue: (provided: any, _state: any) => ({
    ...provided,
    color: '#4c5263',
  }),
  menuList: (provided: any, _state: any) => ({ ...provided, color: '#4c5263' }),
};

interface SelectOwnProps {
  fieldName: string;
  options: object[];
  onChange: (e: any) => void;
  onBlur?: (e: any) => void;
  disabled?: boolean;
  getOptionValue?: (option: any) => string;
  getOptionLabel?: (option: any) => string;
  setValue?: UseFormSetValue<any>;
  value: string;
}

type SelectProps = SelectOwnProps;

export const Select: React.FC<SelectProps> = (props) => {
  const {
    fieldName,
    options,
    setValue,
    onChange,
    onBlur,
    value,
    disabled,
    getOptionValue,
    getOptionLabel,
  } = props;

  return (
    <>
      <ReactSelect
        id={get(ids, fieldName)}
        name={fieldName}
        className="react-select"
        classNamePrefix="react-select"
        components={{ DropdownIndicator }}
        options={options}
        isMulti={false}
        onFocus={() => {
          document
            .getElementsByName(fieldName)
            .item(0)
            ?.dispatchEvent(
              new FocusEvent('focusin', {
                bubbles: true,
              }),
            );
        }}
        onChange={(selectedOption: any) => {
          onChange(selectedOption);
          if (setValue) {
            setValue(fieldName, selectedOption ? selectedOption.value : '', {
              shouldValidate: true,
            });
          }
        }}
        onBlur={(e) => {
          document
            .getElementsByName(fieldName)
            .item(0)
            ?.dispatchEvent(
              new FocusEvent('focusout', {
                bubbles: true,
              }),
            );
          if (onBlur) {
            onBlur(e);
          }
        }}
        value={options.find((o: any) => o.value === value) || null}
        placeholder={get(placeholders(), fieldName)}
        isDisabled={disabled}
        isSearchable={false}
        isClearable={false}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        styles={customStyles}
      />

      {/* autoComplete 用の隠し input */}
      <input
        style={{ height: 0, width: 0 }}
        type="text"
        autoComplete={get(autoCompletes, fieldName)}
        onFocus={() => {
          // one password のような入力補助ツールはこのinputにフォーカスを当てる
          document
            .getElementsByName(fieldName)
            .item(0)
            ?.dispatchEvent(
              new FocusEvent('focusin', {
                bubbles: true,
              }),
            );
        }}
        onBlur={() => {
          // onFocus と同様
          document
            .getElementsByName(fieldName)
            .item(0)
            ?.dispatchEvent(
              new FocusEvent('focusout', {
                bubbles: true,
              }),
            );
        }}
        onChange={(e) => {
          const option = options
            .map((v) => ({
              label: getOptionLabel?.(v) ?? (v as any).label,
              value: getOptionValue?.(v) ?? (v as any).value,
            }))
            .find(
              (v) => v.label === e.target.value || v.value === e.target.value,
            );
          if (option) {
            onChange(option);
            if (setValue) {
              setValue(fieldName, option.value, {
                shouldValidate: true,
              });
            }
          }
        }}
      />
    </>
  );
};
Select.displayName = 'Select';

interface RawSelectOwnProps extends SelectOwnProps {
  value: any;
}

type RawSelectProps = RawSelectOwnProps;

export const RawSelect: React.FC<RawSelectProps> = (props) => {
  const {
    fieldName,
    options,
    onChange,
    value,
    disabled,
    getOptionLabel,
    getOptionValue,
  } = props;

  return (
    <ReactSelect
      id={get(ids, fieldName)}
      name={fieldName}
      className="react-select"
      classNamePrefix="react-select"
      components={{ DropdownIndicator }}
      options={options}
      isMulti={false}
      onChange={onChange}
      value={value}
      placeholder={get(placeholders(), fieldName)}
      isDisabled={disabled}
      isSearchable={false}
      isClearable={false}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      styles={customStyles}
    />
  );
};
RawSelect.displayName = 'RawSelect';

const toOptions = (num: number) => {
  const str = `${num}`;
  return { value: str, label: str };
};

const MAX_YEAR_OPTION = new Date().getFullYear();

export const birthYearOptions = map(range(1900, MAX_YEAR_OPTION), toOptions);
export const birthMonthOptions = map(range(1, 13), toOptions);

export const useBirthDay = (
  yearFieldName: string,
  monthFieldName: string,
  dayFieldName: string,
  getValues: UseFormGetValues<any>,
  setValue: UseFormSetValue<any>,
) => {
  const birthYear = getValues(yearFieldName);
  const birthMonth = getValues(monthFieldName);
  const birthDay = getValues(dayFieldName);

  const date = new Date(+birthYear, +birthMonth, 0);
  const lastDay = date.getDate();
  const birthDayOptionValues = range(1, lastDay + 1).map((i) => i.toString());
  const birthDayOptions = map(birthDayOptionValues, toOptions);

  // 日選択→月選択の操作をした場合に存在しない組み合わせになる可能性があるためその場合は日をclearする
  // （1/31を選択後、月を2月に変更する等）
  React.useEffect(() => {
    if (birthDay !== '' && !birthDayOptionValues.includes(birthDay)) {
      setValue(dayFieldName, '', { shouldValidate: true });
    }
  }, [
    birthDayOptionValues,
    birthYear,
    birthDay,
    birthMonth,
    dayFieldName,
    getValues,
    setValue,
  ]);

  return birthDayOptions;
};
